import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import cx from 'classnames';
import './index.scss';

const MINUTES_IN_DAY = 24 * 60;

const toDate = (value) => {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);

  return Number.isNaN(date.getTime()) ? null : date;
};

// "2:30 PM" — the format the field has always displayed (react-datepicker's "h:mm aa").
export const formatClock = (hours, minutes) => {
  const hours12 = hours % 12 === 0 ? 12 : hours % 12;

  return `${hours12}:${String(minutes).padStart(2, '0')} ${hours < 12 ? 'AM' : 'PM'}`;
};

export const formatTime = (value) => {
  const date = toDate(value);

  return date ? formatClock(date.getHours(), date.getMinutes()) : '';
};

/**
 * Reads the time a person typed. Accepts the shapes people actually type:
 * "2:30 PM", "2:30pm", "230 pm", "2 pm", "14:30", "1430", "2".
 * `isPmFallback` keeps the currently selected half of the day when the typed
 * text has no AM/PM of its own, so editing only the minutes never flips 2 PM to 2 AM.
 * Returns { hours, minutes } in 24h form, or null when the text is not a time.
 */
export const parseTime = (raw, isPmFallback) => {
  if (raw === null || raw === undefined) return null;

  const text = String(raw).trim().toLowerCase().replace(/\./g, '').replace(/\s+/g, ' ');
  if (!text) return null;

  const withMinutes = text.match(/^(\d{1,2})\s*:?\s*(\d{2})\s*(am|pm|a|p)?$/);
  const hourOnly = text.match(/^(\d{1,2})\s*(am|pm|a|p)?$/);
  const match = withMinutes || hourOnly;
  if (!match) return null;

  let hours = parseInt(match[1], 10);
  const minutes = withMinutes ? parseInt(match[2], 10) : 0;
  const period = withMinutes ? match[3] : match[2];

  if (minutes > 59) return null;

  if (period) {
    if (hours < 1 || hours > 12) return null;
    hours = (hours % 12) + (period[0] === 'p' ? 12 : 0);
  } else if (hours > 23) {
    return null;
  } else if (hours >= 1 && hours <= 12 && isPmFallback !== undefined) {
    hours = (hours % 12) + (isPmFallback ? 12 : 0);
  }

  return { hours, minutes };
};

const withTime = (base, hours, minutes) => {
  const next = new Date((toDate(base) || new Date()).getTime());

  next.setHours(hours, minutes, 0, 0);

  return next;
};

const ClockIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.7" />
    <path d="M12 7.5V12L15 13.75" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/**
 * Time field: a drop-down to pick the hour, and an input that stays editable so
 * the minutes, the hour or the AM/PM can be typed over afterwards.
 *
 * Replaces the default react-datepicker time list, which offered the same hours
 * but left the input read-only — so a picked time could never be corrected.
 */
export default function TimeCalendar({
  id,
  value = null,
  onChange = () => {},
  onFocus = () => {},
  readOnly = false,
  disabled = false,
  placeholder = 'Choose Time',
  className = 'form-control',
  inputStyle,
  wrapperStyle,
  interval = 60,
  minuteStep = 5,
  popoverZIndex,
  showIcon = true,
  name,
}) {
  const rootRef = useRef(null);
  const inputRef = useRef(null);
  const popoverRef = useRef(null);
  const listRef = useRef(null);
  const programmaticFocusRef = useRef(false);
  const [isOpen, setIsOpen] = useState(false);
  const [placement, setPlacement] = useState({ above: false, alignEnd: false });
  // Raw text while the field is being typed in; null means "show the picked time".
  const [draft, setDraft] = useState(null);

  const selected = toDate(value);
  const isInteractive = !readOnly && !disabled;
  const selectedAt = selected ? selected.getHours() * 60 + selected.getMinutes() : null;

  const options = useMemo(() => {
    const step = Math.max(5, Math.min(MINUTES_IN_DAY, interval));
    const list = [];

    for (let minutes = 0; minutes < MINUTES_IN_DAY; minutes += step) {
      list.push(minutes);
    }

    return list;
  }, [interval]);

  // The row the list opens on: the picked time when it is one of the options, and
  // otherwise the hour it falls inside — a typed 2:37 PM still opens at 2:00 PM.
  const scrollTo = useMemo(() => {
    if (selectedAt === null) return null;

    let target = null;

    options.forEach((minutes) => {
      if (minutes <= selectedAt) target = minutes;
    });

    return target;
  }, [options, selectedAt]);

  const commitDraft = useCallback(
    (text) => {
      setDraft(null);
      if (text === null || text === undefined) return;

      const trimmed = String(text).trim();

      // An emptied field clears the time instead of snapping back to the old one.
      // `null` is the same empty state a field carries before anyone touches it.
      if (!trimmed) {
        if (selected) onChange(null);

        return;
      }

      if (trimmed === formatTime(selected)) return;

      const parsed = parseTime(trimmed, selected ? selected.getHours() >= 12 : undefined);
      if (!parsed) return;

      onChange(withTime(selected, parsed.hours, parsed.minutes));
    },
    [onChange, selected],
  );

  const close = useCallback(({ refocus = false } = {}) => {
    setIsOpen(false);

    if (refocus && inputRef.current) {
      programmaticFocusRef.current = true;
      inputRef.current.focus();
    }
  }, []);

  const selectOption = useCallback(
    (minutesFromMidnight) => {
      setDraft(null);
      onChange(withTime(selected, Math.floor(minutesFromMidnight / 60), minutesFromMidnight % 60));
      close({ refocus: true });
    },
    [close, onChange, selected],
  );

  // Close (and keep whatever was typed) when the click lands anywhere else.
  useEffect(() => {
    if (!isOpen) return undefined;

    const handlePointerDown = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        commitDraft(draft);
        close();
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
    };
  }, [close, commitDraft, draft, isOpen]);

  // Flip the panel up / to the right when it would otherwise run off screen.
  useLayoutEffect(() => {
    if (!isOpen || !popoverRef.current || !rootRef.current) return;

    const field = rootRef.current.getBoundingClientRect();
    const panel = popoverRef.current.getBoundingClientRect();

    setPlacement({
      above: field.bottom + panel.height + 8 > window.innerHeight && field.top > panel.height,
      alignEnd: field.left + panel.width + 8 > window.innerWidth && field.right > panel.width,
    });
  }, [isOpen]);

  // Open on the picked time instead of leaving the list at midnight.
  useLayoutEffect(() => {
    if (!isOpen || !listRef.current) return;

    const option = listRef.current.querySelector('[data-scroll-to="true"]');
    if (!option) return;

    listRef.current.scrollTop = Math.max(0, option.offsetTop - listRef.current.clientHeight / 2 + option.clientHeight / 2);
  }, [isOpen, scrollTo]);

  // Escape is handled on the root so it also works while focus sits on an option.
  const handleKeyDown = (event) => {
    if (event.key !== 'Escape' || !isOpen) return;

    event.stopPropagation();
    setDraft(null);
    close({ refocus: true });
  };

  const handleInputKeyDown = (event) => {
    if (!isInteractive) return;

    if (event.key === 'Enter') {
      event.preventDefault();
      commitDraft(event.target.value);
      close();

      return;
    }

    // Nudging the minutes without retyping the whole time.
    if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
      event.preventDefault();

      const parsed = parseTime(draft ?? formatTime(selected), selected ? selected.getHours() >= 12 : undefined);
      const base = parsed ? withTime(selected, parsed.hours, parsed.minutes) : selected || new Date();
      const shift = (event.key === 'ArrowUp' ? 1 : -1) * Math.max(1, minuteStep);

      setDraft(null);
      onChange(new Date(base.getTime() + shift * 60 * 1000));
    }
  };

  return (
    <div
      className={cx('time-calendar', { 'is-open': isOpen, 'is-readonly': !isInteractive })}
      ref={rootRef}
      style={wrapperStyle}
      onKeyDown={handleKeyDown}
    >
      <div className={cx('time-calendar__field', { 'has-icon': showIcon && isInteractive })}>
        <input
          id={id}
          name={name}
          type="text"
          autoComplete="off"
          className={className}
          style={inputStyle}
          ref={inputRef}
          value={draft ?? formatTime(selected)}
          placeholder={isInteractive ? placeholder : ''}
          readOnly={!isInteractive}
          disabled={disabled}
          onChange={(event) => isInteractive && setDraft(event.target.value)}
          onFocus={(event) => {
            // Focus handed back by close({ refocus }) is ours, not the person's. It must
            // run none of the focusing behaviour: not reopening the panel they just
            // dismissed, and not the caller's onFocus, whose empty-field auto-fill would
            // otherwise land in the same batch as the pick and overwrite it.
            if (programmaticFocusRef.current) {
              programmaticFocusRef.current = false;

              return;
            }

            onFocus(event);

            if (isInteractive) setIsOpen(true);
          }}
          onClick={() => isInteractive && setIsOpen(true)}
          onBlur={(event) => {
            // Blurring into the list is still "inside" the field.
            if (rootRef.current && rootRef.current.contains(event.relatedTarget)) return;
            commitDraft(event.target.value);
          }}
          onKeyDown={handleInputKeyDown}
        />
        {showIcon && isInteractive && (
          <span className="time-calendar__icon">
            <ClockIcon />
          </span>
        )}
      </div>

      {isOpen && isInteractive && (
        <div
          className={cx('time-calendar__popover', { 'is-above': placement.above, 'is-align-end': placement.alignEnd })}
          style={popoverZIndex ? { zIndex: popoverZIndex } : undefined}
          ref={popoverRef}
        >
          <ul className="time-calendar__list" role="listbox" aria-label="Time" ref={listRef}>
            {options.map((minutes) => {
              const isSelected = minutes === selectedAt;

              return (
                <li key={minutes}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    data-scroll-to={minutes === scrollTo ? 'true' : undefined}
                    className={cx('time-calendar__option', { 'is-selected': isSelected })}
                    onClick={() => selectOption(minutes)}
                  >
                    {formatClock(Math.floor(minutes / 60), minutes % 60)}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
